import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Sparkles, UtensilsCrossed, CalendarDays, Loader2 } from "lucide-react";

const DIETARY_OPTIONS = ["Vegetarian", "Vegan", "Gluten-Free", "Halal", "Kosher", "Dairy-Free", "Nut-Free"];
const MEAL_TYPES_AI = ["All meals", "Breakfast", "AM Break", "Lunch", "PM Break", "Dinner", "All Day Snacks"];

export default function AIPage() {
  const { authAxios } = useAuth();
  const [mealLoading, setMealLoading] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [mealResult, setMealResult] = useState("");
  const [scheduleResult, setScheduleResult] = useState("");

  const [mealForm, setMealForm] = useState({
    event_title: "", num_attendees: 50, dietary_preferences: [],
    budget_per_person: "", meal_type: "", days: 1
  });
  const [scheduleForm, setScheduleForm] = useState({
    event_title: "", num_days: 1, start_time: "09:00",
    end_time: "17:00", session_topics: "", num_attendees: 50
  });

  const toggleDietary = (pref) => {
    setMealForm({
      ...mealForm,
      dietary_preferences: mealForm.dietary_preferences.includes(pref)
        ? mealForm.dietary_preferences.filter((p) => p !== pref)
        : [...mealForm.dietary_preferences, pref]
    });
  };

  const handleMealSuggestion = async (e) => {
    e.preventDefault();
    setMealLoading(true);
    setMealResult("");
    try {
      const api = authAxios();
      const payload = {
        ...mealForm,
        budget_per_person: mealForm.budget_per_person ? parseFloat(mealForm.budget_per_person) : null,
        meal_type: mealForm.meal_type === "All meals" ? null : mealForm.meal_type || null,
      };
      const res = await api.post("/ai/meal-suggestions", payload);
      setMealResult(res.data.suggestions);
      toast.success("Meal suggestions generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to get suggestions");
    } finally {
      setMealLoading(false);
    }
  };

  const handleScheduleSuggestion = async (e) => {
    e.preventDefault();
    setScheduleLoading(true);
    setScheduleResult("");
    try {
      const api = authAxios();
      const payload = {
        ...scheduleForm,
        session_topics: scheduleForm.session_topics.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const res = await api.post("/ai/schedule-suggestions", payload);
      setScheduleResult(res.data.schedule);
      toast.success("Schedule suggestions generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to get suggestions");
    } finally {
      setScheduleLoading(false);
    }
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 w-full space-y-8" data-testid="ai-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-foreground flex items-center gap-3">
          <Sparkles className="h-8 w-8 text-primary" />
          AI Assistant
        </h1>
        <p className="text-muted-foreground mt-1">Get AI-powered meal suggestions and smart scheduling</p>
      </div>

      <Tabs defaultValue="meals" className="w-full" data-testid="ai-tabs">
        <TabsList className="mb-6">
          <TabsTrigger value="meals" data-testid="ai-meals-tab">
            <UtensilsCrossed className="h-4 w-4 mr-2" /> Meal Suggestions
          </TabsTrigger>
          <TabsTrigger value="schedule" data-testid="ai-schedule-tab">
            <CalendarDays className="h-4 w-4 mr-2" /> Smart Scheduling
          </TabsTrigger>
        </TabsList>

        <TabsContent value="meals">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-border/50 shadow-sm" data-testid="meal-suggestion-form">
              <CardHeader>
                <CardTitle className="font-heading text-xl">Meal Planning Input</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleMealSuggestion} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Event Title</Label>
                    <Input
                      placeholder="Annual Community Gathering"
                      value={mealForm.event_title}
                      onChange={(e) => setMealForm({ ...mealForm, event_title: e.target.value })}
                      required data-testid="ai-meal-event-title"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Attendees</Label>
                      <Input
                        type="number" min="1"
                        value={mealForm.num_attendees}
                        onChange={(e) => setMealForm({ ...mealForm, num_attendees: parseInt(e.target.value) || 1 })}
                        data-testid="ai-meal-attendees"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Days</Label>
                      <Input
                        type="number" min="1" max="7"
                        value={mealForm.days}
                        onChange={(e) => setMealForm({ ...mealForm, days: parseInt(e.target.value) || 1 })}
                        data-testid="ai-meal-days"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Budget per Person ($)</Label>
                    <Input
                      type="number" step="0.01" placeholder="Optional"
                      value={mealForm.budget_per_person}
                      onChange={(e) => setMealForm({ ...mealForm, budget_per_person: e.target.value })}
                      data-testid="ai-meal-budget"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Meal Type Focus</Label>
                    <Select value={mealForm.meal_type} onValueChange={(v) => setMealForm({ ...mealForm, meal_type: v })}>
                      <SelectTrigger data-testid="ai-meal-type-select">
                        <SelectValue placeholder="All meals" />
                      </SelectTrigger>
                      <SelectContent>
                        {MEAL_TYPES_AI.map((m) => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Dietary Preferences</Label>
                    <div className="flex flex-wrap gap-2" data-testid="ai-dietary-options">
                      {DIETARY_OPTIONS.map((pref) => (
                        <Badge
                          key={pref}
                          variant={mealForm.dietary_preferences.includes(pref) ? "default" : "outline"}
                          className="cursor-pointer transition-colors"
                          onClick={() => toggleDietary(pref)}
                        >
                          {pref}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <Button type="submit" className="w-full" disabled={mealLoading} data-testid="ai-meal-submit">
                    {mealLoading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating...</>
                    ) : (
                      <><Sparkles className="h-4 w-4 mr-2" /> Get Meal Suggestions</>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="border-border/50 shadow-sm" data-testid="meal-suggestion-result">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <UtensilsCrossed className="h-5 w-5 text-primary" />
                  Suggestions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {mealResult ? (
                  <div className="prose prose-sm dark:prose-invert max-w-none max-h-[60vh] overflow-y-auto" data-testid="meal-result-content">
                    <div className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">{mealResult}</div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <UtensilsCrossed className="h-12 w-12 mx-auto text-muted-foreground/20 mb-3" />
                    <p className="text-muted-foreground">Fill in the form and click generate to get AI-powered meal suggestions</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="schedule">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-border/50 shadow-sm" data-testid="schedule-suggestion-form">
              <CardHeader>
                <CardTitle className="font-heading text-xl">Schedule Input</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleScheduleSuggestion} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Event Title</Label>
                    <Input
                      placeholder="Community Leadership Summit"
                      value={scheduleForm.event_title}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, event_title: e.target.value })}
                      required data-testid="ai-schedule-event-title"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Number of Days</Label>
                      <Input
                        type="number" min="1" max="7"
                        value={scheduleForm.num_days}
                        onChange={(e) => setScheduleForm({ ...scheduleForm, num_days: parseInt(e.target.value) || 1 })}
                        data-testid="ai-schedule-days"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Attendees</Label>
                      <Input
                        type="number" min="1"
                        value={scheduleForm.num_attendees}
                        onChange={(e) => setScheduleForm({ ...scheduleForm, num_attendees: parseInt(e.target.value) || 1 })}
                        data-testid="ai-schedule-attendees"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Time</Label>
                      <Input
                        type="time" value={scheduleForm.start_time}
                        onChange={(e) => setScheduleForm({ ...scheduleForm, start_time: e.target.value })}
                        data-testid="ai-schedule-start-time"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Time</Label>
                      <Input
                        type="time" value={scheduleForm.end_time}
                        onChange={(e) => setScheduleForm({ ...scheduleForm, end_time: e.target.value })}
                        data-testid="ai-schedule-end-time"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Session Topics (comma-separated)</Label>
                    <Textarea
                      placeholder="Welcome, Team Building, Budget Review, Community Planning"
                      value={scheduleForm.session_topics}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, session_topics: e.target.value })}
                      rows={3} data-testid="ai-schedule-topics"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={scheduleLoading} data-testid="ai-schedule-submit">
                    {scheduleLoading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating...</>
                    ) : (
                      <><Sparkles className="h-4 w-4 mr-2" /> Get Schedule Suggestions</>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="border-border/50 shadow-sm" data-testid="schedule-suggestion-result">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <CalendarDays className="h-5 w-5 text-primary" />
                  Schedule
                </CardTitle>
              </CardHeader>
              <CardContent>
                {scheduleResult ? (
                  <div className="prose prose-sm dark:prose-invert max-w-none max-h-[60vh] overflow-y-auto" data-testid="schedule-result-content">
                    <div className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">{scheduleResult}</div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CalendarDays className="h-12 w-12 mx-auto text-muted-foreground/20 mb-3" />
                    <p className="text-muted-foreground">Fill in the form and click generate to get AI-powered scheduling</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
